import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Download, Mail, AlertTriangle } from "lucide-react";
import { api, API } from "@/lib/api";
import { claimPurchase, trackEvent } from "@/lib/analytics";
import { VaultNextStep } from "@/components/VaultNextStep";

export default function StarterKitOrder() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/starter-kit/order/${token}`);
        setO(data);
        if (["paid", "delivered"].includes(data.status)) {
          claimPurchase(api, new URLSearchParams(window.location.search).get("session_id"));
        }
        if (["checkout_started", "paid"].includes(data.status) && tries++ < 40) timer = setTimeout(poll, 2500);
      } catch { setErr("We couldn't find this order."); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [token]);

  const resend = async () => { await api.post(`/starter-kit/order/${token}/resend`); setSent(true); };
  const pdf = (kind) => `${API}/starter-kit/order/${token}/pdf?kind=${kind}`;

  return (
    <main className="max-w-2xl mx-auto px-5 py-16" data-testid="sk-order-page">
      <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--vt-amber)] font-[IBM_Plex_Mono]">Starter Kit</p>
      {err && <p className="mt-4 text-[#B3261E]" data-testid="sk-order-error">{err}</p>}
      {!o && !err && <p className="mt-6 flex items-center gap-2 text-[color:var(--vt-body-on-cream)]"><Loader2 className="animate-spin" size={16} /> Loading your order…</p>}
      {o && (
        <>
          <h1 className="font-head uppercase text-4xl sm:text-5xl text-[color:var(--vt-dark)] mt-2" data-testid="sk-order-title">
            {o.status === "delivered" ? (o.is_gift ? "Gift delivered." : "Your kit is ready, Coach.") : o.status === "needs_attention" ? "We're finishing your kit." : "Building your kit…"}
          </h1>
          <p className="mt-3 text-[color:var(--vt-body-on-cream)]" data-testid="sk-order-combo">{o.sport} · Ages {o.age} · {o.practice_length}-min practices{o.team_name ? ` · ${o.team_name}` : ""}</p>
          {["checkout_started", "paid"].includes(o.status) && (
            <p className="mt-8 flex items-center gap-2" data-testid="sk-order-pending"><Loader2 className="animate-spin text-[color:var(--vt-amber)]" size={18} /> Confirming payment and building your PDFs. This takes a few seconds.</p>
          )}
          {o.status === "needs_attention" && (
            <div className="mt-8 border-2 border-[color:var(--vt-amber)] p-4 flex gap-3" data-testid="sk-order-attention">
              <AlertTriangle className="text-[color:var(--vt-amber)] shrink-0" />
              <p className="text-sm">Your payment went through. Before we send your kit, a coach is giving it a final check. It'll arrive at <b>{o.email}</b> shortly. No need to do anything.</p>
            </div>
          )}
          {o.status === "delivered" && (
            <div className="mt-10 space-y-4">
              <p className="text-sm text-[color:var(--vt-body-on-cream)]" data-testid="sk-order-sent-to">{o.is_gift ? <>We've emailed the kit to the coach at <b>{o.email}</b>, along with your note. You'll get a receipt too. You can also download both PDFs here.</> : <>We've emailed both PDFs to <b>{o.email}</b>. You can also download them here anytime.</>}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <a data-testid="sk-order-dl-guide" onClick={() => trackEvent("kit_download", { kind: "guide", sport: o.sport, age: o.age })} href={pdf("guide")} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[color:var(--vt-dark)] text-[color:var(--vt-cream)] font-head uppercase py-3 border-2 border-[color:var(--vt-dark)] hover:bg-[color:var(--vt-amber)] hover:text-[color:var(--vt-dark)] transition-colors"><Download size={16} /> Coach's Guide</a>
                <a data-testid="sk-order-dl-cards" onClick={() => trackEvent("kit_download", { kind: "cards", sport: o.sport, age: o.age })} href={pdf("cards")} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 font-head uppercase py-3 border-2 border-[color:var(--vt-dark)] text-[color:var(--vt-dark)] hover:bg-[color:var(--vt-dark)]/5 transition-colors"><Download size={16} /> Practice Cards</a>
              </div>
              <button data-testid="sk-order-resend" onClick={resend} disabled={sent} className="flex items-center gap-2 text-sm underline text-[color:var(--vt-body-on-cream)] disabled:no-underline">
                <Mail size={14} /> {sent ? "Sent again. Check your inbox." : "Didn't get the email? Resend it"}
              </button>
              <VaultNextStep from="starter_kit_order" />
            </div>
          )}
        </>
      )}
    </main>
  );
}
